/** One section of the tour record (plan §1 "Body"): a `SectionCard` holding the
 *  section's Editable field groups, then — under a hairline — its System block of
 *  derived values. The same DOM in view and edit mode: FieldSlots swap their control part
 *  only. The panel is the section's anchor (`tour#<section>`, the old tab slugs land
 *  here).
 *
 *  Values the panel renders itself (view mode): a stored image as a downsized thumbnail,
 *  the coordinates as one "lat, lng" value, and the value actions of links, addresses and
 *  picked records. */
import { Box, Chip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { SectionCard } from '@/components/ReadView';
import { isHttpUrl } from '@/components/ValueActions/FieldActionsAdornment';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { BlockRule } from '@/features/records/panels/BlockRule';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { HEADING_ROW_PX } from '@/features/records/panels/headingRow';

import {
  addressActions,
  departureSlotActions,
  externalUrlActions,
  latLngActions,
  launchPolicyActions,
  routeSetActions,
} from '../../tourLinks';
import { CONTEXT_URL_PATHS, TOUR_ENTITY } from '../catalog';
import { formatLength } from '../length';
import { blockAnchorId, groupKeys, sectionAnchorId, type TourBlockId, type TourSectionDef } from '../registry';
import { useTourRecord } from '../TourRecordContext';
import { ImageDisplay } from '../widgets';

/** Field groups that are a block anchor of their own (the launch band's Guest app node
 *  lands on Basics' Chat & app group; the old Departures tab slug on the departures). */
const GROUP_BLOCKS: Partial<Record<string, TourBlockId>> = { chat: 'guest-app', departures: 'departures' };

/** Widgets whose input wants the whole row. */
const WIDE_WIDGETS = new Set(['tags', 'textarea', 'text_lazy', 'precheck_fields', 'precheck_categories', 'precheck_waivers']);
/** Widgets that need two field columns (a thumbnail with its buttons, two inputs). */
const TWO_COLUMN_WIDGETS = new Set(['image', 'coordinates']);

const URL_KEYS = new Set([
  'meeting_point.maps_url',
  'review.url',
  'photo_sharing.url',
  'positive_review_url',
  ...CONTEXT_URL_PATHS,
]);

function fieldActions(key: string): ((value: unknown) => ValueAction[]) | undefined {
  if (URL_KEYS.has(key)) {
    return (v) => (typeof v === 'string' && isHttpUrl(v.trim()) ? externalUrlActions(v.trim(), 'Open the link') : []);
  }
  if (key === 'meeting_point.address') return (v) => (typeof v === 'string' && v.trim() ? addressActions(v.trim()) : []);
  return undefined;
}

export interface TourSectionPanelProps {
  section: TourSectionDef;
  /** Under a group's fields, by group id. */
  afterGroup?: Partial<Record<string, ReactNode>>;
  /** At the end of a group's heading row, by group id — an edit-mode action whose row is
   *  reserved in both modes (Zero-Re-Layout). */
  groupAction?: Partial<Record<string, ReactNode>>;
  /** Right side of the section header. */
  meta?: ReactNode;
}

export function TourSectionPanel({ section, afterGroup, groupAction, meta }: TourSectionPanelProps) {
  const { detail } = useTourRecord();
  const groups = section.groups
    .map((g) => ({ ...g, keys: groupKeys(section.id, g.id, 'editable', detail) }))
    .filter((g) => g.keys.length > 0 || afterGroup?.[g.id]);
  const system = section.groups.flatMap((g) => groupKeys(section.id, g.id, 'system', detail));

  return (
    <Box
      component="section"
      id={sectionAnchorId(section.id)}
      tabIndex={-1}
      aria-label={section.title}
      data-tour-section={section.id}
      sx={{
        scrollMarginTop: 'var(--record-spine-height, 0px)',
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        '& > .MuiCard-root': { mb: 0 },
      }}
    >
      <SectionCard title={section.title} action={meta}>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {groups.map((g) => (
            <Box
              key={g.id}
              sx={{
                display: 'grid',
                gap: 0.5,
                scrollMarginTop: 'var(--record-spine-height, 0px)',
                outline: 'none',
                borderRadius: 1,
                '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
              }}
              data-field-group={g.id}
              {...(GROUP_BLOCKS[g.id]
                ? {
                    id: blockAnchorId(GROUP_BLOCKS[g.id]!),
                    tabIndex: -1,
                    'data-tour-block': GROUP_BLOCKS[g.id],
                    component: 'section',
                    'aria-label': g.title,
                  }
                : {})}
            >
              {g.title && groupAction?.[g.id] !== undefined ? (
                <Box data-block-heading sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: HEADING_ROW_PX, minWidth: 0 }}>
                  <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600, flex: 1, minWidth: 0 }}>
                    {g.title}
                  </Typography>
                  {groupAction[g.id]}
                </Box>
              ) : (
                g.title && (
                  <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
                    {g.title}
                  </Typography>
                )
              )}
              {g.keys.length > 0 && <TourFieldGrid keys={g.keys} />}
              {afterGroup?.[g.id]}
            </Box>
          ))}
          {system.length > 0 && (
            <BlockRule title="System" note="derived" rule={groups.length > 0}>
              <TourFieldGrid keys={system} />
            </BlockRule>
          )}
        </Box>
      </SectionCard>
    </Box>
  );
}

export function TourFieldGrid({
  keys,
  displays,
  labels,
}: {
  keys: string[];
  /** A key's view-mode rendering of its stored value (a list as a list). */
  displays?: Partial<Record<string, ReactNode>>;
  /** A key's label on the page when the catalog's (the ledger's) names more context. */
  labels?: Partial<Record<string, string>>;
}) {
  const { fieldByKey } = useRecordEditor();
  const { detail, links } = useTourRecord();
  const id = detail.id;
  const rec = `${TOUR_ENTITY}:${id}`;

  const displayOf = (key: string): ReactNode | undefined => {
    if (displays && key in displays) return displays[key];
    const field = fieldByKey.get(key);
    if (field?.widget === 'image') return <ImageDisplay path={key} label={field.label} />;
    if (key === 'departures') {
      // Each stored departure with its quick actions (its tag, its manifest, its logs).
      const deps = (detail.departures ?? []).filter((d) => d.tag);
      if (deps.length === 0) return null;
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} data-departures>
          {deps.map((d) => (
            <ValueActions key={d.tag!.id} chip actions={departureSlotActions({ tagId: d.tag!.id, name: d.tag!.name }, detail.id, links)}>
              <Chip size="small" variant="outlined" label={d.tag!.name} />
            </ValueActions>
          ))}
        </Box>
      );
    }
    if (key === 'length') {
      const len = formatLength(detail.length);
      return detail.length_is_manual ? `${len} · set by hand` : `${len} · derived`;
    }
    if (key === 'meeting_point.latitude') {
      const mp = detail.meeting_point;
      if (mp?.latitude == null || mp.longitude == null) return null;
      return `${mp.latitude}, ${mp.longitude}`;
    }
    return undefined;
  };
  const actionsOf = (key: string): ((value: unknown) => ValueAction[]) | undefined => {
    if (key === 'launch_policy') {
      return (v) => {
        const ref = typeof v === 'number' ? { id: v, name: detail.launch_policy?.id === v ? detail.launch_policy.name : null } : null;
        return ref ? launchPolicyActions(ref, links) : [];
      };
    }
    if (key === 'vehicle_route_set') {
      return (v) =>
        typeof v === 'number' ? routeSetActions({ id: v, name: detail.vehicle_route_set?.name ?? null }, links) : [];
    }
    if (key === 'meeting_point.latitude') {
      const mp = detail.meeting_point;
      return () => (mp?.latitude != null && mp.longitude != null ? latLngActions({ lat: mp.latitude, lng: mp.longitude }) : []);
    }
    return fieldActions(key);
  };

  return (
    <FieldCellGrid
      sx={FIELD_GRID_SX}
      cells={keys.map((key) => {
        const field = fieldByKey.get(key);
        const display = displayOf(key);
        const actions = actionsOf(key);
        const wide = Boolean(field?.widget && WIDE_WIDGETS.has(field.widget));
        return {
          key: `${rec}:${key}`,
          rec,
          path: key,
          wide,
          ...(field?.widget && TWO_COLUMN_WIDGETS.has(field.widget) ? { span: 2 } : {}),
          node: (
            <FieldSlot
              fieldKey={key}
              entity={TOUR_ENTITY}
              id={id}
              path={key}
              inlineChoices={false}
              {...(labels?.[key] ? { label: labels[key] } : {})}
              {...(display !== undefined ? { display } : {})}
              {...(actions ? { actions } : {})}
            />
          ),
        };
      })}
    />
  );
}
